import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, FormControlStatus, UntypedFormGroup } from '@angular/forms';
import { USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import {
    IAccountFilter,
    IFinancialStatementInterpretation,
    NumberFormats,
    Signs,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal';
import { distinctUntilChanged } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FinancialStatementImportInterpretationHelper } from '../../helpers/financial-statement-import-interpretation.helper';

const VALID_FORM = 'VALID';

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-financial-statement-import-interpretation',
    templateUrl: 'financial-statement-import-interpretation.component.html',
    styleUrls: ['financial-statement-import-interpretation.component.scss'],
})
export class FinancialStatementImportInterpretationComponent implements OnInit {
    interpretationForm: UntypedFormGroup;
    accountFilterForm: UntypedFormGroup;

    // For edit mode
    @Input() interpretationData: IFinancialStatementInterpretation;
    @Input() accountFilterData: IAccountFilter;

    @Output() interpretationFormValueChangesEvent = new EventEmitter<IFinancialStatementInterpretation>();
    @Output() accountFilterFormValueChangesEvent = new EventEmitter<IAccountFilter>();
    @Output() accountFilterFormStatusChangesEvent = new EventEmitter<boolean>();

    SignEnum = Signs;
    NumberFormatEnum = NumberFormats;

    constructor(private formBuilder: UntypedFormBuilder) {}

    ngOnInit(): void {
        this.initInterpretationForm();
        this.initAccountFilterForm();
    }

    private initInterpretationForm(): void {
        this.interpretationForm = this.formBuilder.group({
            numberFormat: [NumberFormats.SINGLE],
            assets: [Signs.POSITIVE],
            liabilities: [Signs.POSITIVE],
            income: [Signs.POSITIVE],
            expenses: [Signs.NEGATIVE],
        });
        if (this.interpretationData != null) {
            this.interpretationForm.get('numberFormat').setValue(this.interpretationData.numberFormat);
            this.interpretationForm.get('assets').setValue(this.interpretationData.assets);
            this.interpretationForm.get('liabilities').setValue(this.interpretationData.liabilities);
            this.interpretationForm.get('income').setValue(this.interpretationData.income);
            this.interpretationForm.get('expenses').setValue(this.interpretationData.expenses);
        }

        this.interpretationFormValueChangesEvent.emit(this.interpretationForm.value);
        this.interpretationForm.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((formValue: IFinancialStatementInterpretation) => {
                this.interpretationFormValueChangesEvent.emit(formValue);
            });
    }

    private initAccountFilterForm(): void {
        this.accountFilterForm = this.formBuilder.group({
            accountNumber: ['', FinancialStatementImportInterpretationHelper.validateAccountNumber()],
            accountName: [''],
            openingValue: [''],
            closingValue: [''],
            excludedWord: [''],
            includedWord: [''],
            removeAccountWithZeroBalance: [false],
        });
        if (this.accountFilterData != null) {
            this.accountFilterForm.get('accountNumber').setValue(this.accountFilterData.accountNumber);
            this.accountFilterForm.get('accountName').setValue(this.accountFilterData.accountName);
            this.accountFilterForm.get('openingValue').setValue(this.accountFilterData.openingValue);
            this.accountFilterForm.get('closingValue').setValue(this.accountFilterData.closingValue);
            this.accountFilterForm.get('excludedWord').setValue(this.accountFilterData.excludedWord);
            this.accountFilterForm.get('includedWord').setValue(this.accountFilterData.includedWord);
            this.accountFilterForm
                .get('removeAccountWithZeroBalance')
                .setValue(this.accountFilterData.removeAccountWithZeroBalance);
        }

        this.accountFilterFormValueChangesEvent.emit(this.accountFilterForm.value);

        this.accountFilterForm.statusChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((formStatus: FormControlStatus) => {
                const isFormValid = formStatus === VALID_FORM;
                this.accountFilterFormStatusChangesEvent.emit(isFormValid);
                if (isFormValid) {
                    this.accountFilterFormValueChangesEvent.emit(this.accountFilterForm.value);
                }
            });
    }
}
